import { format } from "date-fns";
import { Trans, useTranslation } from "react-i18next";
import { SPOTButton } from "spot-component-library/lib/components/SPOTButton";
import { Panel } from "spot-component-library/lib/components/basic/panel/Panel";
import singlePaymentImage from "../../../resources/images/EOS_simple_illustration_service_debt_collection.svg";
import { PageTitle } from "../../components/PageTitle/PageTitle";
import { mapPaymentMethod } from "../../utils/paymentMethods";

import FormatNumber, { formattedNumber } from "../../components/FormatNumber";
import { PaymentFormData } from "../../types/PaymentFormData";
import { PaymentType } from "../../types/PaymentType";
import { RateData } from "../../types/RateData";
import "./SuccessPage.scss";
import { useCurrency } from "../../contexts/TenantContext";

interface SuccessPageProp {
    paymentFormData: PaymentFormData;
    rateData: RateData;
    remainingBalance?: number;
}

export const SuccessPage = (props: SuccessPageProp) => {
    const { t } = useTranslation();
    const currency = useCurrency();

    const firstPaymentDate = props.rateData.dateOfFirstPay ? format(props.rateData.dateOfFirstPay, "dd.MM.yyyy") : "";

    const redirectToSinglePayment = () => {
        // @ts-ignore expecting that etracker already included in the window object
        const etracker = window["_etracker"];

        if (etracker && etracker.isTrackingEnabled()) {
            try {
                // @ts-ignore
                etracker.sendEvent(new et_ClickEvent(et_pagename, "SinglePaymentClick"));
            } catch (err) {
                console.warn("ETC error!", err);
            }
        }
        return (window.location.href = `./payments/#debtPage`);
        //return (window.location.href = `/index.xhtml?debtno=${props.rateData.debtNumber}`);
    };

    return (
        <div>
            <PageTitle icon="icon-finish" iconColor="success" title={t("SUCCESS_PAGE.TITLE")} />

            <Panel>
                <div data-test="success-page" id={"success-page"}>
                    <div className={"whiteBlock"}>
                        <h3 className={"summary-title dark-font"}>{t("SUCCESS_PAGE.OVERVIEW_TITLE")}</h3>

                        <div className={"success-summary"}>
                            <div className={"row-wrapper"}>
                                <div>{t("SUCCESS_PAGE.RATE_AMOUNT")}</div>
                                <b>
                                    <span data-test="rateAmount">{<FormatNumber number={props.rateData.rateAmount} currency={currency} />}</span>
                                </b>
                            </div>

                            <div className={"row-wrapper"}>
                                <div>{t("SUCCESS_PAGE.NUMBER_OF_RATES")}</div>
                                <b>
                                    <span data-test="rateNumber">{props.rateData.rateNumber}</span>
                                </b>
                            </div>

                            <div className={"row-wrapper"}>
                                <div>{t("SUCCESS_PAGE.DATE_OF_FIRST_RATE")}</div>
                                <b>
                                    <span data-test="dateOfFirstPay">{firstPaymentDate}</span>
                                </b>
                            </div>

                            <div className={"row-wrapper"}>
                                <div>{t("SUCCESS_PAGE.PAYMENT_INTERVAL")}</div>
                                <b>
                                    <span data-test="interval">{t("SUCCESS_PAGE.MONTHLY")}</span>
                                </b>
                            </div>

                            <div className={"row-wrapper"}>
                                <div>{t("SUCCESS_PAGE.PAYMENT_TYPE")}</div>
                                <b>
                                    <span data-test="paymentType">{t(mapPaymentMethod(props.paymentFormData.paymentType))}</span>
                                </b>
                            </div>

                            <hr className={"separator"} />

                            {(props.paymentFormData.paymentType == PaymentType.ABO_DIRECTDEBIT || props.paymentFormData.paymentType == PaymentType.ABO_BANKTRANSFER) && (
                                <>
                                    <div className={"row-wrapper"}>
                                        <b>{t("SUCCESS_PAGE.BANK_DETAILS")}</b>
                                    </div>

                                    <div className={"row-wrapper"}>
                                        <div>{t("SUCCESS_PAGE.ACCOUNT_HOLDER")}</div>
                                        <b>
                                            <span data-test="kontoinhaber">{props.paymentFormData.kontoinhaber}</span>
                                        </b>
                                    </div>

                                    <div className={"row-wrapper"}>
                                        <div>{t("SUCCESS_PAGE.BANK")}</div>
                                        <b>
                                            <span data-test="bank">{props.paymentFormData.kreditinstitut}</span>
                                        </b>
                                    </div>

                                    <div className={"row-wrapper"}>
                                        <div>{t("SUCCESS_PAGE.IBAN")}</div>
                                        <b>
                                            <span data-test="iban">{props.paymentFormData.iban}</span>
                                        </b>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <b>
                        <h3 className={"dark-font"}>{t("SUCCESS_PAGE.NEXT_STEPS_TITLE")}</h3>
                    </b>

                    <div className={"row-wrapper-baseline"}>
                        <p>1.</p>
                        <p>{t("SUCCESS_PAGE.NEXT_STEPS_ONE")}</p>
                    </div>

                    <div className={"row-wrapper-baseline"}>
                        <p>2.</p>
                        <p data-test="paymentMethod">
                            {props.paymentFormData.paymentType == PaymentType.ABO_DIRECTDEBIT && (
                                <Trans
                                    i18nKey="SUCCESS_PAGE.NEXT_STEPS_TWO_DIRECTDEBIT"
                                    values={{
                                        rateAmount: formattedNumber(props.rateData.rateAmount),
                                        iban: props.paymentFormData.iban,
                                    }}
                                />
                            )}
                            {props.paymentFormData.paymentType == PaymentType.ABO_BANKTRANSFER && (
                                <Trans
                                    i18nKey="SUCCESS_PAGE.NEXT_STEPS_TWO_BANKTRANSFER"
                                    values={{
                                        rateAmount: formattedNumber(props.rateData.rateAmount),
                                    }}
                                />
                            )}
                            {props.paymentFormData.paymentType == PaymentType.ABO_PAYPAL && (
                                <Trans
                                    i18nKey="SUCCESS_PAGE.NEXT_STEPS_TWO_PAYPAL"
                                    values={{
                                        rateAmount: formattedNumber(props.rateData.rateAmount),
                                    }}
                                />
                            )}
                            {props.paymentFormData.paymentType == PaymentType.ABO_CREDITCARD && (
                                <Trans
                                    i18nKey="SUCCESS_PAGE.NEXT_STEPS_TWO_CREDITCARD"
                                    values={{
                                        rateAmount: formattedNumber(props.rateData.rateAmount),
                                    }}
                                />
                            )}
                        </p>
                    </div>

                    <div className={"row-wrapper-baseline"}>
                        <p>3.</p>
                        <p data-test="remainingBalance">
                            <Trans
                                i18nKey="SUCCESS_PAGE.NEXT_STEPS_THREE"
                                values={{
                                    remainingBalance: formattedNumber(props.remainingBalance),
                                }}
                            />
                        </p>
                    </div>
                </div>
            </Panel>

            <div className={"box-single-payment greyBlock"}>
                <img className={"single-payment-image"} src={singlePaymentImage} alt="SinglePaymentImage" />

                <div className={"single-payment-text-block"}>
                    <h3 className={"dark-font"}>{t("SUCCESS_PAGE.SINGLE_PAYMENT_TITLE")}</h3>
                    <div className={"single-payment-text"}>{t("SUCCESS_PAGE.SINGLE_PAYMENT_TEXT")}</div>
                </div>

                <SPOTButton id={"buttonToOneTimePayment"} type="ghost" label={t("SUCCESS_PAGE.MAKE_ONE_OFF_PAYMENT")} onClick={() => redirectToSinglePayment()} />
            </div>
        </div>
    );
};
